import { useCallback, useState } from "react"
import {
	IconGridLayoutRowsOutline18 as FlatViewIcon,
	IconGridLayoutColsOutline18 as SplitViewIcon,
} from "nucleo-ui-outline-18"

import { SegmentedControl } from "@productlane/ui"

import type { ReviewDiffLayout } from "./review-diff"
import { readReviewViewMode } from "./review-diff"

export type ReviewViewMode = ReviewDiffLayout

// Shared by the docs review and the Agent page, so a reviewer's choice follows them.
const VIEW_MODE_KEY = "docs-review-layout-v3"

/** The reviewer's split or stacked diff choice, remembered in this browser. */
export function useReviewViewMode(): [
	ReviewViewMode,
	(mode: ReviewViewMode) => void,
] {
	const [mode, setMode] = useState<ReviewViewMode>(() =>
		readReviewViewMode(
			typeof localStorage === "undefined"
				? null
				: localStorage.getItem(VIEW_MODE_KEY),
		),
	)
	const update = useCallback((next: ReviewViewMode) => {
		setMode(next)
		try {
			localStorage.setItem(VIEW_MODE_KEY, next)
		} catch {
			/* private mode - the choice just won't persist */
		}
	}, [])
	return [mode, update]
}

const VIEW_MODE_OPTIONS = [
	{ value: "split" as const, label: "Split", Icon: SplitViewIcon },
	{ value: "flat" as const, label: "Flat", Icon: FlatViewIcon },
]

export function ViewModeToggle({
	value,
	onChange,
}: {
	value: ReviewViewMode
	onChange: (mode: ReviewViewMode) => void
}) {
	return (
		<SegmentedControl
			name="review-view-mode"
			value={value}
			options={VIEW_MODE_OPTIONS}
			onValueChange={onChange}
		/>
	)
}
